/**
 * Per-tab notes about a guest visit. NOT the guest scope.
 *
 * Whether a session is a guest session is answered by the access token's
 * `guestQuoteId` claim — see `guest-token.ts`. That has to be the source: the
 * session travels in a cookie every tab on this origin shares, so anything kept
 * here is invisible to a second tab, and a guest opening `/quotes` there used
 * to get the whole platform.
 *
 * What is left here is genuinely tab-local: the emailed link's token, so a
 * lapsed session can re-run the code challenge in place, and the orders this
 * visit produced, so accepting can lead somewhere. Neither is a permission.
 *
 * Lives in sessionStorage so it survives reloads but dies with the tab.
 */

const GUEST_SESSION_KEY = 'codezo_guest_session';

interface GuestSession {
	/** The quote the guest arrived for. */
	orderQuoteId: string;
	/**
	 * The opaque `?token=` from the emailed link. Kept so an expired session can
	 * re-run the code challenge instead of dumping the guest on a login page for
	 * an account they don't have. It's no more secret than the URL they arrived
	 * on, and it dies with the tab.
	 */
	linkToken?: string;
	/**
	 * The user the session hydrated into, recorded once known. Lets us tell a
	 * stale marker apart from a live guest if someone signs in normally in the
	 * same tab afterwards.
	 */
	userId?: string;
	/**
	 * Orders this guest's acceptance produced. A quote turns into an order that
	 * still needs paying, so the guest has to be able to reach it — and come
	 * back to it after a reload.
	 */
	orderIds?: string[];
	/**
	 * True when the tab was opened from an admin's *preview* link rather than a
	 * real acceptance link.
	 *
	 * Nothing in the session itself says so — the whole point is that the guest
	 * screens behave identically — so this marker is the only way the app knows
	 * to show the preview banner and to disable the actions the backend will
	 * refuse. It also rules out the recovery path: a preview token is burned on
	 * first use, so a lapsed preview has no challenge to fall back on.
	 */
	preview?: boolean;
}

export function startGuestSession(orderQuoteId: string, linkToken: string): void {
	writeGuestSession({ orderQuoteId, linkToken });
}

/**
 * Same marker, for an admin previewing the guest view. No `linkToken`: the
 * preview token is single-use, so keeping it would only offer a retry that
 * cannot work.
 */
export function startGuestPreviewSession(orderQuoteId: string): void {
	writeGuestSession({ orderQuoteId, preview: true });
}

/** Record an order the guest just created, so the guest routes will show it. */
export function addGuestOrder(orderId: string): void {
	const session = getGuestSession();
	if (!session) return;
	const orderIds = session.orderIds ?? [];
	if (orderIds.includes(orderId)) return;
	writeGuestSession({ ...session, orderIds: [...orderIds, orderId] });
}

export function getGuestSession(): GuestSession | null {
	try {
		const raw = sessionStorage.getItem(GUEST_SESSION_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as GuestSession;
		return parsed?.orderQuoteId ? parsed : null;
	} catch {
		return null;
	}
}

/** Pin the marker to the user it hydrated into, the first time we see them. */
export function bindGuestSessionUser(userId: string): void {
	const session = getGuestSession();
	if (!session || session.userId) return;
	writeGuestSession({ ...session, userId });
}

export function clearGuestSession(): void {
	try {
		sessionStorage.removeItem(GUEST_SESSION_KEY);
	} catch {
		// ignore
	}
}

export function guestQuotePath(orderQuoteId: string): string {
	return `/guest/quotes/${orderQuoteId}`;
}

export function guestOrderPath(orderId: string): string {
	return `/guest/orders/${orderId}`;
}

function writeGuestSession(session: GuestSession): void {
	try {
		sessionStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
	} catch {
		// Private-mode/quota failures are non-fatal — the guest just sees the
		// full shell instead of the scoped one.
	}
}
